import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const fetchCategory = async (categoryId) => {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("id, name")
      .eq("id", categoryId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export function useEmptyState({
  size = "Desktop 1440",
  type = "Discover SubCategory",
  categoryId = 1
}) {
  const { handleError } = useQueryError();
  
  const { data: category, isLoading, error } = useQuery({
    queryKey: queryKeys.categories.byId(categoryId),
    queryFn: () => fetchCategory(categoryId),
    onError: handleError,
    staleTime: 1000 * 60 * 60, // 1 hora - las categorías cambian raramente
    cacheTime: 1000 * 60 * 60 * 24, // 24 horas
    enabled: Boolean(categoryId)
  });

  const getHorizontalWidth = () => {
    switch (size) {
      case "Desktop 1440":
      case "Desktop 1200":
        return 626;
      case "Tablet":
        return 500;
      case "Phone":
        return 330;
      default:
        return 626;
    }
  };

  const getContent = () => {
    const categoryName = category?.name || "";
    
    switch (type) {
      case "Discover SubCategory":
        return {
          title: `No ${categoryName} projects to explore yet.`,
          subtitle: "We're cooking up some exciting additions. Check back soon! In the meantime, why not discover other cool projects?",
          buttonText: "Discover",
          buttonLink: "/discover/all"
        };
      case "Trending":
        return {
          title: "Trends are taking a breather.",
          subtitle: "Popular projects will be making waves here shortly.",
          buttonText: "",
          buttonLink: ""
        };
      case "Trending SubCategory":
        return {
          title: `${categoryName} trends are taking a breather.`,
          subtitle: "Popular projects will be making waves here shortly. While you wait, how about checking out what's hot in other categories?",
          buttonText: "See what's hot",
          buttonLink: "/trending/all"
        };
      case "JustIn":
        return {
          title: "Our newcomers are fashionably late.",
          subtitle: "Fresh projects are on their way to make their grand entrance.",
          buttonText: "",
          buttonLink: ""
        };
      case "JustIn SubCategory":
        return {
          title: `Our ${categoryName} newcomers are fashionably late.`,
          subtitle: "Fresh projects are on their way to make their grand entrance. Until then, why not see what's new in other areas?",
          buttonText: "See what's new",
          buttonLink: "/just_in/all"
        };
      case "Launched":
        return {
          title: "Launches are preparing for takeoff.",
          subtitle: "Soon, you'll see newly launched ventures reaching for the stars.",
          buttonText: "",
          buttonLink: ""
        };
      case "Launched SubCategory":
        return {
          title: `${categoryName} launches are preparing for takeoff.`,
          subtitle: "Soon, you'll see newly launched ventures reaching for the stars. While we wait, how about exploring other launched projects?",
          buttonText: "View launches",
          buttonLink: "/launched/all"
        };
      default:
        return {
          title: "",
          subtitle: "",
          buttonText: "",
          buttonLink: ""
        };
    }
  };

  return {
    width: getHorizontalWidth(),
    content: getContent(),
    isLoading,
    error
  };
}